<template>
    <div>
        <v-container class="ma-n3">
            <v-row no-gutters align="center" justify="space-between">
                <h2>Service History</h2>
                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn small tile v-on="on"  class="ma-n6">
                            <v-icon>save_alt</v-icon>&nbsp;Export
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="exportCsv()">
                            <v-list-item-title>
                                <v-icon>grid_on</v-icon>&nbsp;Excel (CSV)
                                <v-chip x-small>limit {{maxRowsPerCsv}} rows</v-chip>
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-row>
        </v-container>
        <v-card>
            <v-data-table
                :items="completedServices"
                :items-per-page="options.itemsPerPage"
                :headers="headers"
                :loading="$wait.is(['loading services'])"
                :footer-props.sync="footerProps"
                :server-items-length="serverCount"
                :options.sync="options"
            >
                <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                <template v-slot:item="items">
                    <tr>
                        <td>
                            {{items.item.date | formatDate}}
                            <div v-if="items.item.manuallyMoved" class="caption">(Manually Moved)</div>
                        </td>
                        <tasks-chip-list v-model="items.item.tasks" show-completed-at-tolerance documents-enabled></tasks-chip-list>
                        <td>{{formattedCompletedAt(items.item.completedAtHrs, items.item.completedAtKms)}}</td>
                        <td>{{items.item.completedBy}}</td>
                        <td v-if="!unit.isDeleted && userIsFleetManager">
                            <v-layout>
                                <v-btn icon small @click="openServiceDialog(items.item)">
                                    <v-icon>edit</v-icon>
                                </v-btn>
                                <v-btn @click="confirmDelete(items.item)" icon small color="red">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </v-layout>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
  import Services from '@/components/Services/ServicesClient.vue';
  import TasksChipList from '@/components/ServiceTasks/ChipList.vue';
  import {Event} from '@/plugins/bus';
  import DownloadHelper from '@/lib/DownloadHelper';
  import moment from 'moment';
  import {DateFormat} from '@/plugins/date-filter';

  export default {
    components: {
      TasksChipList
    },
    mixins: [Services],
    props: {
      unit: {required: true}
    },
    data() {
      return {
        completedServices: [],
        serverCount: 0,
        maxRowsPerCsv: 4000,
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['date']
        },
        footerProps: {
          itemsPerPageOptions: [10, 50, 100]
        },
        headers: [
          {text: 'Date', value: 'date', sortable: false},
          {text: 'Tasks', value: 'tasks', sortable: false},
          {text: 'Completed At', value: 'completedAt', sortable: false},
          {text: 'Completed By', value: 'completedBy', sortable: false},
          {text: '', value: 'actions', sortable: false}
        ],
        model: {}
      };
    },
    computed: {
      unitId() {
        return this.$route.params.unitId;
      },
      params() {
        return {
          pageSize: this.options && this.options.itemsPerPage,
          page: this.options && this.options.page,
          completed: true,
          orderBy: 'date',
          unitId: this.unitId,
          orderByDescending: true,
        };
      }
    },
    methods: {
      openServiceDialog(service) {
        this.$router.push({query: {serviceId: service.id}});
      },
      async loadCompletedServices() {
        let results = await this.getUnitServices(this.unitId);
        this.completedServices = results ? this.sortServicesAndTasks(results.data) : [];
        this.serverCount = results ? results.total : 0;
      },
      formattedCompletedAt(completedAtHrs, completedAtKms) {
        const hrsValue = completedAtHrs != null ? completedAtHrs + ' hrs' : '';
        const kmsValue = completedAtKms != null ? completedAtKms + ' kms' : '';
        return hrsValue + (!!hrsValue ? '  ' : '') + kmsValue;
      },
      confirmDelete(completedService) {
        this.$confirmCancel.show(`Delete Completed Service?`, `<p>This will permanently delete this completed service and all of its tasks</p>`, 'Delete it');
        this.$confirmCancel.$on('confirm-cancel', confirmed => {
          if (!confirmed) {
            return;
          }
          this.$bus.$emit(Event.ServicePendingDelete, {
            id: completedService.id
          });
        });
      },
      async exportCsv() {
        let searchParams = {
          completed: true,
          orderBy: 'date',
          orderByDescending: true,
          page: 1,
          pageSize: this.maxRowsPerCsv,
          unitId: this.unitId
        };

        const query = '?' + Object.keys(searchParams).map((key) => key + '=' + searchParams[key]).join('&');
        let results = await this.getUnitServices(this.unitId, query);
        let sortedResults = results ? this.sortServicesAndTasks(results.data) : [];

        const mapTasks = x => x.tasks.map(t => t.shortName + (t.completedAtTolerance || t.completedAtTolerance === 0 ? `(${t.completedAtTolerance > 0 ? '+' : ''}${t.completedAtTolerance})` : '')).join(',');
        const rows = sortedResults.map(x => [x.date, x.completedAtHrs || x.completedAtKms || 0, x.completedBy, mapTasks(x), x.hoursWorked]);
        const csv = DownloadHelper.makeCsv(['Date', 'Completed At', 'Completed By', 'Tasks', 'Time Taken'], rows);
        DownloadHelper.download(`${this.unit.number}_Services_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
      }
    },
    async mounted() {
      this.loadCompletedServices();
      this.$bus.$on(Event.ServiceCompleted, this.loadCompletedServices);
      this.$bus.$on(Event.DocumentsUpdated, this.loadCompletedServices);
      this.$bus.$on(Event.ServiceDeleted, this.loadCompletedServices);
    },
    destroyed() {
      this.$bus.$off(Event.ServiceCompleted, this.loadCompletedServices);
      this.$bus.$off(Event.DocumentsUpdated, this.loadCompletedServices);
      this.$bus.$off(Event.ServiceDeleted, this.loadCompletedServices);
    },
    watch: {
      options: {
        handler() {
          this.loadCompletedServices();
        },
        deep: true
      },
      async unitId(val) {
        this.loadCompletedServices();
      }
    }
  };
</script>
<style>
    td {
        cursor: pointer;
    }
</style>
